import { useEffect, useState } from 'react';
import axios from 'axios';
import { isBlockedAnswer, markBlocked } from './blocked';
import { contractorSignOut } from './contractorAuth';

/**
 * Who the SERVER says you are.
 *
 * The app used to decide this from localStorage('user_role'), defaulting to
 * 'admin' - so every visitor was an administrator as far as the interface was
 * concerned, and anyone could promote themselves by typing one line into the
 * browser console. The API no longer believes any of that, so the interface
 * should not either: it asks, once, and shares the answer.
 *
 * This still only decides what is shown. It is a courtesy to the person using
 * the app, never the protection - the server checks every request regardless.
 *
 * TWO KINDS OF "NO". A 2xx (or a 401) saying `signedIn: false` is the server's
 * answer: nobody is signed in. A request that never got an answer - a timeout,
 * a 502 from the proxy, a 5xx - is not an answer at all, and used to be
 * treated as the same thing: `.catch(() => ({ signedIn: false }))`. One blip
 * on /auth/me and every route redirected to the front door, which offered
 * "Sign in with Microsoft" to somebody who was signed in, and the result was
 * cached so it never recovered until a reload. Now a failed request keeps
 * whatever was known before, says so (useMeStatus), and tries again with a
 * growing wait until the server answers.
 *
 * STAFF IDENTITY WINS. When the server says a member of staff is signed in,
 * any contractor token left in this browser is forgotten here - on a shared
 * machine a contractor who never signed out would otherwise hijack every
 * shared route (/my-tickets, /create, /login...) for the next person. The
 * long version of why a stale token is dangerous is in IncomingCall.js.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let cached = null;
let inflight = null;
const listeners = new Set();

// Whether the last attempt failed to reach the server, and how many in a row.
let status = { failed: false, attempt: 0 };
const statusListeners = new Set();
let retryTimer = null;

const setStatus = (next) => {
  status = next;
  statusListeners.forEach((fn) => { try { fn(status); } catch { /* a listener must not break this */ } });
};

// 2s, 4s, 8s, 16s, then every 30s.
const retryDelay = (attempt) => Math.min(30000, 2000 * (2 ** Math.max(0, attempt - 1)));

const scheduleRetry = () => {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    fetchMe({ force: true });
  }, retryDelay(status.attempt));
};

// The moment the browser says it is back online is the moment to ask again.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('online', () => {
    if (status.failed) {
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      fetchMe({ force: true });
    }
  });
}

/** The server answered, even if with a refusal: a 4xx carries its verdict. */
const isAnswer = (err) => {
  const s = err?.response?.status;
  return Boolean(s) && s < 500 && s !== 408 && s !== 429;
};

export function fetchMe({ force = false } = {}) {
  if (!force && cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = axios.get(`${API_BASE}/auth/me`)
    .then((r) => ({ me: r.data }))
    .catch((err) => (isAnswer(err)
      ? { me: { ...(err.response?.data && typeof err.response.data === 'object' ? err.response.data : {}), signedIn: false } }
      : { failed: true }))
    .then(({ me, failed }) => {
      inflight = null;
      if (failed) {
        setStatus({ failed: true, attempt: status.attempt + 1 });
        scheduleRetry();
        if (!cached) {
          // Nothing known yet: the app cannot wait forever on "Signing you
          // in…", so it opens as signed out - marked `failed`, so the routes'
          // own tidying (clearing a stored View as, say) knows this is not
          // the server's word - and keeps asking until it is.
          cached = { signedIn: false, failed: true };
          listeners.forEach((fn) => fn(cached));
        }
        return cached;
      }
      if (status.failed) setStatus({ failed: false, attempt: 0 });
      // Switched off by an administrator: the app shows one page instead.
      if (isBlockedAnswer(200, me)) markBlocked();
      if (me?.signedIn && me.staffSession === true) contractorSignOut();
      cached = me;
      listeners.forEach((fn) => fn(me));
      return me;
    });
  return inflight;
}

export function useMe() {
  const [me, setMe] = useState(cached);
  useEffect(() => {
    let live = true;
    listeners.add(setMe);
    fetchMe().then((m) => { if (live) setMe(m); });
    return () => { live = false; listeners.delete(setMe); };
  }, []);
  return me;
}

/** `{ failed, attempt }`: whether /auth/me is currently unreachable. */
export function useMeStatus() {
  const [s, setS] = useState(status);
  useEffect(() => {
    statusListeners.add(setS);
    setS(status);
    return () => { statusListeners.delete(setS); };
  }, []);
  return s;
}

/** The last answer, without asking - for code outside React (the interceptor). */
export function currentMe() { return cached; }
